import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, AppWindow, Activity, Clock, FileText, Settings,
  ChevronRight, LogOut, User, Menu, X, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const NAV_ITEMS = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/system-health', icon: Activity, label: 'System Health', adminOnly: true },
  { to: '/apps', icon: AppWindow, label: 'App Registry' },
  { to: '/activity', icon: Activity, label: 'Activity Feed' },
  { to: '/automations', icon: Clock, label: 'Automations' },
  { to: '/posts', icon: FileText, label: 'Draft Posts' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`glass-sidebar flex-shrink-0 flex flex-col transition-[width] duration-300 ${sidebarOpen ? 'w-64' : 'w-16'}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-white/5">
          <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0 glow-primary">
            <Zap className="w-4 h-4 text-primary" />
          </div>
          {sidebarOpen && (
            <div className="fade-in">
              <span className="font-heading font-bold text-sm text-foreground tracking-wide">MAYR OPS</span>
              <div className="text-xs text-muted-foreground font-mono">Operations Cockpit</div>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="ml-auto text-muted-foreground hover:text-foreground p-1 rounded transition-colors"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1 px-2 overflow-y-auto">
          {NAV_ITEMS.filter(item => !item.adminOnly || user?.is_owner || user?.is_super_admin).map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group relative ${
                  isActive
                    ? 'bg-primary/15 text-primary border border-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary rounded-r-full" />
                  )}
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {sidebarOpen && <span className="text-sm font-medium slide-in">{label}</span>}
                  {sidebarOpen && isActive && <ChevronRight className="w-3 h-3 ml-auto opacity-60" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/5 p-3">
          {sidebarOpen ? (
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors">
              <div className="w-7 h-7 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-foreground truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate font-mono">{user?.email}</div>
              </div>
              <button
                onClick={handleLogout}
                className="text-muted-foreground hover:text-destructive transition-colors p-1"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex justify-center text-muted-foreground hover:text-destructive transition-colors p-2"
              data-testid="logout-btn-collapsed"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto" data-testid="main-content">
        <div className="max-w-[1400px] mx-auto p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
